import { CompanySelect } from "components/inputs/CompanySelect";
import { SelectEnumInput } from "components/inputs/SelectEnumInput";
import { DayOfWeek } from "entities/DayOfWeek";
import { RevenueSource } from "entities/RevenueSource";
import { useEffect } from "react";
import { Filter, useListContext, useTranslate } from "react-admin";

import { DateInput } from "../../../components/inputs/DateTimeInput";
import { useTerminalsContext } from "../../../contexts/TerminalsProvider";
import { getDateWithDayOffset, getDateWithMonthOffset } from "../../../utils/datetime";
import { ServiceInputRaw } from "../../orders/forms/inputs/ServiceInput";
import { ArticleAutocompleteInputAdvanced } from "../../orders/Show/Pricelist/ArticleAutocompleteInput";
import { GroupingPeriodFilter } from "./GroupingPeriodFilter";
import { GroupingPeriodEnum } from "./GroupingPeriodFilter";
import { RevenueSourceFilter } from "./RevenueSourceFilter";

const GROUPING_PERIOD_FILTER_CONFIG = {
	[GroupingPeriodEnum.MONTH]: {
		getDateFrom: (terminal) => getDateWithMonthOffset(terminal, 12, true),
		getDateTo: (terminal) => getDateWithMonthOffset(terminal),
	},
	[GroupingPeriodEnum.WEEK]: {
		getDateFrom: (terminal) => getDateWithDayOffset(terminal, 7 * 12, true),
		getDateTo: (terminal) => getDateWithDayOffset(terminal),
	},
	[GroupingPeriodEnum.DAY]: {
		getDateFrom: (terminal) => getDateWithDayOffset(terminal, 7, true),
		getDateTo: (terminal) => getDateWithDayOffset(terminal),
		extraFilters: { day_of_week: DayOfWeek.ALL },
	},
};

export const RevenueAndExpensesChartFilter = (props) => {
	const translate = useTranslate();

	const { filterValues, setFilters } = useListContext();

	const { data: terminals } = useTerminalsContext();
	const onGroupingPeriodChange = (groupingPeriod) => {
		const terminal = terminals[props.terminalId];
		const config = GROUPING_PERIOD_FILTER_CONFIG[groupingPeriod[0]];
		if (!config) return;

		const { day_of_week, ...restAllFilters } = filterValues;

		const newFilters = {
			date_from: config.getDateFrom(terminal),
			date_to: config.getDateTo(terminal),
			...(config.extraFilters || {}),
		};

		setFilters({ ...restAllFilters, grouping_period: groupingPeriod, ...newFilters });
	};

	useEffect(() => {
		if (
			filterValues.revenue_source === RevenueSource.WEEKLY_STORAGE &&
			filterValues.service != null
		) {
			const { service, ...rest } = filterValues;
			setFilters(rest);
		}
	}, [filterValues.revenue_source, filterValues.service]);

	const isDayOfWeekFilterVisible = filterValues?.grouping_period?.[0] === GroupingPeriodEnum.DAY;
	const isServiceFilterVisible = filterValues?.revenue_source !== RevenueSource.WEEKLY_STORAGE;

	return (
		<Filter {...props}>
			<GroupingPeriodFilter
				source="grouping_period"
				defaultValue={null}
				onChange={onGroupingPeriodChange}
				alwaysOn
				key={"group_period"}
			/>
			<RevenueSourceFilter
				source="revenue_source"
				label="resources.financial-statements.filters.revenue_source"
				alwaysOn
				key={"revenue_source"}
			/>
			{isDayOfWeekFilterVisible && (
				<SelectEnumInput
					source="day_of_week"
					resettable
					enumName="day-of-week"
					enumObj={DayOfWeek}
					style={{ minWidth: "240px" }}
					alwaysOn
					allowEmpty={false}
				/>
			)}
			{[
				<DateInput
					source="date_from"
					label={translate("osfc.filters.dates.date_from")}
					margin="dense"
					key={"date_from"}
					alwaysOn
				/>,
				<DateInput
					source="date_to"
					label={translate("osfc.filters.dates.date_to")}
					margin="dense"
					key={"date_to"}
					alwaysOn
				/>,
			]}
			{isServiceFilterVisible && (
				<ServiceInputRaw
					source="service"
					resettable
					key={"service"}
					style={{ minWidth: "240px" }}
					alwaysOn
				/>
			)}
			<CompanySelect
				source="company_id"
				emptyValue={null}
				allowEmpty
				resettable
				key={"company_id"}
				alwaysOn
			/>
			<ArticleAutocompleteInputAdvanced
				source="article_code"
				terminal_id={props.terminalId}
				company_id={filterValues?.company_id}
				label="resources.financial-statements.filters.price_article"
				onItemChange={() => {}}
				key={"article_code"}
				style={{ minWidth: "240px" }}
				margin="dense"
				alwaysOn
			/>
		</Filter>
	);
};
